import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { showErrorToast } from '../../utils/toast';
import { logger } from '../../utils/logger';
import { setupInterceptors } from './interceptors';
import { setupApiMonitoring } from './monitoring.service';
import {
  ESTABLISHMENT_HEADER,
  ESTABLISHMENT_STORAGE_KEY,
  resolveRequestEstablishmentId,
} from './establishment-scope';

// Get the base URL from environment variable or default to localhost
const BASE_URL = import.meta.env['VITE_API_URL'] || 'http://localhost:3333/api';

// Custom params serializer to handle arrays without brackets
// NestJS expects: confirmationStatuses=value1&confirmationStatuses=value2
// Not: confirmationStatuses[]=value1&confirmationStatuses[]=value2
const paramsSerializer = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer,
});

// Request interceptor to add JWT token and establishment header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage (or wherever it's stored)
    const token = localStorage.getItem('mep_token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add establishment header - always read from localStorage to ensure latest value
    // This is critical for ensuring the correct establishment is used after switching
    const activeEstablishmentId = localStorage.getItem(ESTABLISHMENT_STORAGE_KEY);
    if (activeEstablishmentId) {
      // Also ensure axios defaults are in sync with localStorage
      // This prevents desynchronization issues when switching establishments.
      // Só o condomínio ativo entra nos defaults: um override de requisição aqui
      // vazaria para todas as chamadas seguintes.
      if (apiClient.defaults.headers.common[ESTABLISHMENT_HEADER] !== activeEstablishmentId) {
        apiClient.defaults.headers.common[ESTABLISHMENT_HEADER] = activeEstablishmentId;
      }
    }

    const establishmentId = resolveRequestEstablishmentId(
      config,
      activeEstablishmentId,
    );
    if (establishmentId) {
      config.headers[ESTABLISHMENT_HEADER] = establishmentId;
    }
    
    // Add timing metadata for performance tracking
    (config as InternalAxiosRequestConfig & { metadata?: { startTime: number } }).metadata = {
      startTime: Date.now()
    };
    
    // Debug logging only for non-GET requests
    if (config.method !== 'get') {
      logger.apiRequest(config.method?.toUpperCase() || 'UNKNOWN', config.url || '', {
        metadata: {
          headers: {
            'Authorization': config.headers.Authorization ? 'Bearer [token]' : 'None',
            'x-establishment-id': config.headers[ESTABLISHMENT_HEADER] || 'None'
          },
          data: config.data
        }
      });
    }
    
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // Only log non-GET responses
    if (response.config.method !== 'get') {
      const startTime = (response.config as InternalAxiosRequestConfig & { metadata?: { startTime: number } })?.metadata?.startTime || Date.now();
      logger.apiResponse(
        response.config.method?.toUpperCase() || 'UNKNOWN',
        response.config.url || '',
        response.status,
        Date.now() - startTime,
        {
          metadata: {
            data: response.data
          }
        }
      );
    }
    return response;
  },
  (error: AxiosError) => {
    // Don't log 401 errors for auth check endpoints
    const isAuthCheck = error.config?.url?.includes('/auth/me') || 
                       error.config?.url?.includes('/config/navigation');
    const is401 = error.response?.status === 401;
    
    if (!(is401 && isAuthCheck)) {
      logger.error('[API Client] Error', error as Error, {
        module: 'api-client',
        metadata: {
          url: error.config?.url,
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        }
      });
    }
    
    // Handle different error scenarios
    if (error.response) {
      const status = error.response.status;
      
      switch (status) {
        case 400: {
          // Bad request
          const badRequestError = error.response.data as { message?: string };
          logger.error('[API Client] Bad Request Error', undefined, {
            module: 'api-client',
            action: 'bad-request',
            metadata: {
              url: error.config?.url,
              params: error.config?.params,
              headers: error.config?.headers,
              data: badRequestError,
            }
          });
          
          // Check for specific pagination errors
          if (badRequestError.message?.includes('page') || badRequestError.message?.includes('limit')) {
            emitErrorToast('Erro nos parâmetros de paginação', {
              action: {
                label: 'Recarregar',
                onClick: () => window.location.reload()
              }
            });
          } else {
            emitErrorToast(badRequestError.message || 'Requisição inválida.');
          }
          break;
        }
          
        case 401: {
          // Check if this request should bypass automatic 401 handling
          interface ConfigWithSkipAutoLogout extends InternalAxiosRequestConfig {
            skipAutoLogout?: boolean;
          }
          const skipAutoLogout = (error.config as ConfigWithSkipAutoLogout)?.skipAutoLogout;
          
          if (!skipAutoLogout) {
            // Check if this is an initial auth check (e.g., /auth/me on app load)
            const isEstablishmentCheck = error.config?.url?.includes('/users/me/establishments');
            const isLoginPage = window.location.pathname === '/' || window.location.pathname === '';
            const isLoginRequest = error.config?.url?.includes('/auth/login');
            
            // Handle login failure specifically
            if (isLoginRequest) {
              // For login requests, don't clear tokens or redirect, just let the error bubble up
              // The login form will handle showing the appropriate error message
              break;
            }
            
            // Clear token and redirect for all 401s except establishment check
            // Auth checks (/auth/me) returning 401 mean the token is expired — must clear session
            // Establishment checks are excluded to avoid cascade issues (AuthContext handles them via meError)
            if (!isEstablishmentCheck) {
              // Unauthorized - clear token and redirect to login
              localStorage.removeItem('mep_token');
              localStorage.removeItem('mep_user');
              localStorage.removeItem('mep_current_establishment');

              // Only show toast if not on login page
              if (!isLoginPage) {
                emitErrorToast('Sessão expirada', {
                  action: {
                    label: 'Fazer Login',
                    onClick: () => window.location.href = '/'
                  },
                  duration: 8000
                });
              }
            }
          }
          break;
        }
          
        case 403:
          // Forbidden - user doesn't have permission
          emitErrorToast('Você não tem permissão para realizar esta ação.', {
            action: {
              label: 'Voltar',
              onClick: () => window.history.back()
            }
          });
          break;
          
        case 409: {
          // Conflict - duplicate resource
          const conflictError = error.response.data as { message?: string };
          emitErrorToast(conflictError.message || 'Este registro já existe.');
          break;
        }

        case 404:
          // Not found
          emitErrorToast('Recurso não encontrado.', {
            action: {
              label: 'Voltar',
              onClick: () => window.history.back()
            }
          });
          break;
          
        case 422: {
          // Validation error
          const validationError = error.response.data as { message?: string };
          logger.error('[API Client] Validation Error Details', undefined, {
            module: 'api-client',
            action: 'validation-error',
            metadata: {
              url: error.config?.url,
              params: error.config?.params,
              headers: error.config?.headers,
              data: validationError,
            }
          });
          if (validationError.message) {
            emitErrorToast(validationError.message);
          } else {
            emitErrorToast('Erro de validação. Verifique os dados informados.');
          }
          break;
        }
          
        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          emitErrorToast('Erro no servidor. Por favor, tente novamente mais tarde.', {
            action: {
              label: 'Tentar Novamente',
              onClick: () => window.location.reload()
            }
          });
          break;
          
        default: {
          // Generic error message
          const genericError = error.response.data as { message?: string };
          emitErrorToast(genericError.message || 'Ocorreu um erro inesperado.');
        }
      }
    } else if (error.request) {
      // Request was made but no response received
      emitErrorToast('Erro de conexão. Verifique sua internet.', {
        // Id estável: várias falhas de conexão seguidas não empilham toasts.
        id: 'api-connection-error',
        action: {
          label: 'Tentar Novamente',
          onClick: () => window.location.reload()
        }
      });
    } else {
      // Something else happened
      emitErrorToast('Ocorreu um erro inesperado.');
    }
    
    return Promise.reject(error);
  }
);

// Make apiClient available globally for EstablishmentContext
declare global {
  interface Window {
    apiClient: AxiosInstance;
  }
}

window.apiClient = apiClient;

// Setup additional interceptors
setupInterceptors(apiClient);

// Setup API monitoring
setupApiMonitoring(apiClient);

const emitErrorToast = (
  message: Parameters<typeof showErrorToast>[0],
  options?: Parameters<typeof showErrorToast>[1],
) => {
  if (typeof window !== 'undefined' && (window as { __DISABLE_API_ERROR_TOAST__?: boolean }).__DISABLE_API_ERROR_TOAST__) {
    return;
  }
  showErrorToast(message, options as Parameters<typeof showErrorToast>[1]);
};

// Export the configured client
export default apiClient;

// Export type for use in other files
export type { AxiosInstance };
